import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef } from 'react';

// Responsive iframe shell: holds aspect ratio without the classic
// padding-bottom hack (aspect-ratio is well supported in our browserslist).
const Frame = styled('div', {
  shouldForwardProp: (p) => p !== '$ratio',
})<{ $ratio: string }>(({ theme, $ratio }) => ({
  margin: theme.spacing(4, 0),
  width: '100%',
  aspectRatio: $ratio,
  '& iframe': {
    width: '100%',
    height: '100%',
    border: 0,
    borderRadius: theme.shape.borderRadius,
  },
}));

/** Generic sandboxed iframe embed. */
export const Embed: React.FC<{
  src: string;
  title?: string;
  ratio?: string;
  allow?: string;
}> = ({ src, title = 'embedded content', ratio = '16 / 9', allow }) => (
  <Frame $ratio={ratio}>
    <iframe
      src={src}
      title={title}
      loading="lazy"
      allow={allow}
      sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
      allowFullScreen
    />
  </Frame>
);

export const YouTube: React.FC<{ id: string; title?: string }> = ({ id, title = 'YouTube video' }) => (
  <Embed
    src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}`}
    title={title}
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
  />
);

export const CodeSandbox: React.FC<{ id: string; title?: string }> = ({ id, title = 'CodeSandbox' }) => (
  <Embed src={`https://codesandbox.io/embed/${encodeURIComponent(id)}`} title={title} ratio="16 / 10" />
);

export const Gist: React.FC<{ user: string; id: string }> = ({ user, id }) => (
  <Embed
    src={`https://gist.github.com/${encodeURIComponent(user)}/${encodeURIComponent(id)}.pibb`}
    title="GitHub gist"
    ratio="4 / 3"
  />
);

/** X / Twitter embed — lazily pulls the official widget script once. */
export const Tweet: React.FC<{ url: string }> = ({ url }) => {
  const ref = useRef<HTMLQuoteElement>(null);

  useEffect(() => {
    const w = window as unknown as { twttr?: { widgets: { load: (el?: HTMLElement) => void } } };
    const render = () => w.twttr?.widgets.load(ref.current ?? undefined);
    if (w.twttr) {
      render();
      return;
    }
    const id = 'twitter-wjs';
    if (!document.getElementById(id)) {
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://platform.twitter.com/widgets.js';
      s.async = true;
      s.onload = render;
      document.body.appendChild(s);
    }
  }, [url]);

  return (
    <Box sx={{ my: 4, display: 'flex', justifyContent: 'center' }}>
      <blockquote className="twitter-tweet" ref={ref}>
        <a href={url}>{url}</a>
      </blockquote>
    </Box>
  );
};
